import { useState } from 'react';
import { Phone, Mail, MapPin, Send, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const Contact = () => {
  const { language, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Send via WhatsApp
    const message = language === 'ar'
      ? `رسالة جديدة من موقع EliteMeat:\n\nالاسم: ${formData.name}\nالبريد: ${formData.email}\nالهاتف: ${formData.phone}\nالشركة: ${formData.company}\nالموضوع: ${formData.subject}\n\nالرسالة:\n${formData.message}`
      : `New message from EliteMeat website:\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCompany: ${formData.company}\nSubject: ${formData.subject}\n\nMessage:\n${formData.message}`;
    
    const whatsappUrl = `https://wa.me/201270706065?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(
      language === 'ar' ? 'تم إرسال الرسالة!' : 'Message Sent!',
      { description: language === 'ar' ? 'شكراً لتواصلك معنا' : 'Thank you for contacting us' }
    );
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {language === 'ar' ? 'اتصل بنا' : 'Contact Us'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {language === 'ar' 
              ? 'تواصل معنا للاستفسارات أو الطلبات الكبيرة أو الشراكات التجارية'
              : 'Get in touch with us for inquiries, bulk orders, or business partnerships'}
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'تواصل معنا' : 'Get In Touch'}
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {language === 'ar'
                  ? 'سواء كنت تاجر جملة أو تجزئة أو موزع، نحن هنا للمساعدة. تواصل معنا اليوم لمناقشة احتياجاتك.'
                  : "Whether you're a wholesaler, retailer, or distributor, we're here to help. Contact us today to discuss your needs."}
              </p>

              <div className="space-y-6">
                <Card className="border-0 shadow-md">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-red-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {language === 'ar' ? 'الهاتف' : 'Phone'}
                      </h3>
                      <a href="tel:01270706065" className="text-gray-700 hover:text-red-600 transition-colors">
                        01270706065
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-green-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {language === 'ar' ? 'واتساب' : 'WhatsApp'}
                      </h3>
                      <a href="https://wa.me/201270706065" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-green-600 transition-colors">
                        +201270706065
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-red-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                      </h3>
                      <a href="mailto:elitemeatfarms@gmail.com" className="text-gray-700 hover:text-red-600 transition-colors break-all">
                        elitemeatfarms@gmail.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-red-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {language === 'ar' ? 'العنوان' : 'Address'}
                      </h3>
                      <a 
                        href="https://maps.app.goo.gl/4gdVrjjNrHJY551R8" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-red-600 transition-colors"
                      >
                        {language === 'ar' 
                          ? '٨٢١ خط ٤ شمال جمعيه احمد عرابي الزراعية'
                          : '821 Line 4 North Orabi Agricultural Farms'}
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'ساعات العمل' : 'Business Hours'}
                </h3>
                <p className="text-gray-700">
                  {language === 'ar' 
                    ? 'السبت - الخميس: ٨:٠٠ صباحاً - ٥:٠٠ مساءً'
                    : 'Saturday - Thursday: 8:00 AM - 5:00 PM'}
                  <br />
                  {language === 'ar' ? 'الجمعة: مغلق' : 'Friday: Closed'}
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'تابعنا' : 'Follow Us'}
                </h3>
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/profile.php?id=61572273943626" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors group">
                    <Facebook size={24} className="text-red-600 group-hover:text-white" />
                  </a>
                  <a href="https://www.instagram.com/elitemeatfarms/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors group">
                    <Instagram size={24} className="text-red-600 group-hover:text-white" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className={isRTL ? 'md:order-1' : ''}>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    {language === 'ar' ? 'أرسل لنا رسالة' : 'Send Us a Message'}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'الاسم الكامل *' : 'Full Name *'}
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'اسمك' : 'Your name'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'البريد الإلكتروني *' : 'Email Address *'}
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'بريدك@email.com' : 'your@email.com'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'رقم هاتفك' : 'Your phone number'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'اسم الشركة' : 'Company Name'}
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'شركتك' : 'Your company'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'الموضوع *' : 'Subject *'}
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'ما هو موضوع رسالتك؟' : 'What is this regarding?'}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'الرسالة *' : 'Message *'}
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={language === 'ar' ? 'أخبرنا عن استفسارك' : 'Tell us about your inquiry'}
                        rows={5}
                        className="w-full"
                      />
                    </div>

                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" size="lg">
                      <MessageCircle className={isRTL ? 'ml-2' : 'mr-2'} size={18} />
                      {language === 'ar' ? 'إرسال عبر واتساب' : 'Send via WhatsApp'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            {language === 'ar' ? 'موقعنا' : 'Our Location'}
          </h2>
          <div className="bg-gray-200 rounded-lg overflow-hidden" style={{ height: '400px' }}>
            <iframe
              title="Location Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3454.2!2d31.2357!3d30.0444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x145847e9cc11e3c9%3A0x88b3461e0c89c8f9!2z2KfZhNis2YXYudio2Kkg2KfZhNmF2LXYsdmK2Kkg2YTZhNiy2LHYp9i52Kk!5e0!3m2!1sar!2seg!4v1706573521234!5m2!1sar!2seg"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div className="text-center mt-4">
            <a 
              href="https://maps.app.goo.gl/4gdVrjjNrHJY551R8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
            >
              <MapPin size={18} />
              {language === 'ar' ? 'فتح في خرائط جوجل' : 'Open in Google Maps'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
